import { supabase, fromWithCompany } from "../config/supabaseClient";
import {
  POPULATION_GROUPS,
  TRAINING_CATEGORY_OPTIONS,
  statuses,
  trainingFormStatus,
  SKILLS_PLANNER_FORM_STATUS_FILTER,
} from "../constant/constants";
import { isUnemployedEmploymentType } from "../constant/utils/employmentTypeUtils";
import { employmentTypesFromSetaFields } from "./skillsPlannerUtils";
import {
  getDisabilityConfig,
  getDisabilityConfigFromFileStructure,
  resolveDisability,
} from "../constant/utils/disabilityStatusUtils";
import { checkTrainingsCriticalStatus } from "./criticalSkillsTrainingUtils";

async function loadEmploymentTypesForCompany(companyId) {
  try {
    const { data, error } = await supabase
      .from("company")
      .select("seta_template(file_structure)")
      .eq("id", companyId)
      .single();

    const setaRow = Array.isArray(data?.seta_template)
      ? data.seta_template[0]
      : data?.seta_template;
    const fs = setaRow?.file_structure;
    if (error || !fs) return [];

    const sheet = Array.isArray(fs?.sheets) ? fs.sheets[0] : null;
    if (Array.isArray(sheet?.employmentTypes) && sheet.employmentTypes.length) {
      return sheet.employmentTypes;
    }
    const fields = sheet?.fields || fs?.fields || [];
    return employmentTypesFromSetaFields(fields) || [];
  } catch {
    return [];
  }
}

async function loadDisabilityConfigForCompany(companyId) {
  try {
    const { data, error } = await supabase
      .from("company")
      .select("seta_template(file_structure)")
      .eq("id", companyId)
      .single();

    const setaRow = Array.isArray(data?.seta_template)
      ? data.seta_template[0]
      : data?.seta_template;

    if (error || !setaRow?.file_structure) {
      return { disabilityOptions: [], disabilityFieldName: null };
    }

    return getDisabilityConfigFromFileStructure(setaRow.file_structure);
  } catch {
    return { disabilityOptions: [], disabilityFieldName: null };
  }
}

/**
 * Fetches training category default values from database configuration
 * @param {string} companyId - Company ID
 * @param {string} yearId - Year ID
 * @returns {Promise<Object>} Object with category names as keys and budget values, e.g., { "Category A": 64000, "Category B": 45000, ... }
 */
export const fetchTrainingCategoryDefaults = async (companyId, yearId) => {
  try {
    const { data, error } = await supabase
      .from("configurations")
      .select("training_category_config")
      .eq("company_id", companyId)
      .eq("year_id", yearId)
      .single();

    if (error) {
      // If no record found, return empty object (will fallback to 0)
      if (error.code === 'PGRST116') {
        return {};
      }
      console.error("Error fetching training category config:", error.message);
      return {};
    }

    // Transform array format to object format
    // Database format: [{ category: "Category A", percentage: 44.14, budget: 64000 }, ...]
    // Expected format: { "Category A": 64000, "Category B": 45000, ... } (budget values)
    const defaults = {};
    if (data?.training_category_config && Array.isArray(data.training_category_config)) {
      data.training_category_config.forEach((item) => {
        if (item.category && item.budget !== undefined) {
          defaults[item.category] = parseFloat(item.budget) || 0;
        }
      });
    }

    return defaults;
  } catch (err) {
    console.error("Error in fetchTrainingCategoryDefaults:", err.message);
    return {};
  }
};

/**
 * Format training survey forms data and restructures it to match exact JSON format
 * @param {Array} forms - Array of training survey form objects
 * @returns {Promise<Array>} Restructured forms array matching the desired format
 */
const FormatTrainingSurveyForms = async (forms, disabilityConfig = {}, companyId = null) => {
  if (!Array.isArray(forms)) return [];
  const { disabilityOptions = [], disabilityFieldName = null } = disabilityConfig;

  return await Promise.all(
    forms.map(async (form) => {
      // Extract employee data
      const empData = form.empData || {};

      const hasDisability = resolveDisability({
        record: empData,
        disabilityOptions,
        disabilityFieldName,
      });

      // Build emp_data with only the required fields
      const emp_data = {
        emp_id: empData.id || null,
        race: empData.race || empData.Race || null,
        disability: hasDisability,
        disability_status: hasDisability,
        employment_type: empData.employment_type || "",
        gender: empData.gender || null,
        stipend_amount: empData.stipend_amount != null ? empData.stipend_amount : null,
      };
      
        // Extract form fields
      const formsObj = form.forms || {};
      const notes = formsObj.notes || form.notes || "";
      const approved = formsObj.approved !== undefined ? formsObj.approved : (form.approved || false);
      const long_term_goal = formsObj.long_term_goal || form.long_term_goal || "";
      const short_term_goal = formsObj.short_term_goal || form.short_term_goal || "";
      const skills_required = formsObj.skills_required || form.skills_required || null;
      
      // Extract skillRequirements - keep id field
      let skillRequirements = [];
      if (form.skillRequirements && Array.isArray(form.skillRequirements)) {
        skillRequirements = form.skillRequirements.map((skill) => {
          return {
            id: skill.id || null,
            priority_level: skill.priority_level || null,
            skills_required: skill.skills_required || null,
            training_type: skill.training_type || null,
            program_duration: skill.program_duration || null,
            skills_requirement_motivation: skill.skills_requirement_motivation || null,
            completion_date: skill.completion_date || null,
          };
        });
      } else if (form.skill_requirements && Array.isArray(form.skill_requirements)) {
        skillRequirements = form.skill_requirements.map((skill) => {
          return {
            id: skill.id || null,
            priority_level: skill.priority_level || null,
            skills_required: skill.skills_required || null,
            training_type: skill.training_type || null,
            program_duration: skill.program_duration || null,
            skills_requirement_motivation: skill.skills_requirement_motivation || null,
            completion_date: skill.completion_date || null,
          };
        });
      }

      // Extract selected_training - flat array of training objects
      let selected_training = [];
      if (form.selectedTrainings && Array.isArray(form.selectedTrainings)) {
        // Check if training data is already fully populated (from transformation)
        // If training_id, name, cost, etc. are already present, use them directly
        const hasFullTrainingData = form.selectedTrainings.some(t => 
          t.training_id && (t.name || t.cost !== undefined)
        );

        if (hasFullTrainingData) {
          // Training data already fetched, use it directly
          selected_training = form.selectedTrainings.map((trainingItem) => {
            const trainingObj = trainingItem.training || trainingItem;
            const skillReqId = trainingItem.skill_requirements_id || trainingItem.skillsRequirementId || 
                              (trainingObj.skill_requirements_id) || null;
            
            return {
              training_id: trainingObj.training_id || trainingObj.id || null,
              catalogue_id: trainingObj.catalogue_id || null,
              cost: trainingObj.cost || null,
              name: trainingObj.name || null,
              training_type: trainingObj.training_type || trainingObj.type || null,
              training_category: trainingObj.training_category || null,
              provider: trainingObj.provider || null,
              is_critical: trainingObj.is_critical !== undefined ? trainingObj.is_critical : false,
              nqf_aligned: trainingObj.nqf_aligned !== undefined ? trainingObj.nqf_aligned : false,
              targeted_skills: trainingObj.targeted_skills || null,
              skillsRequirementId: skillReqId,
            };
          });
        } else {
          // Training data not fully populated, fetch it
          const trainingIds = form.selectedTrainings
            .map(trainingItem => {
              const trainingObj = trainingItem.training || trainingItem;
              return trainingObj.training_id || trainingObj.id;
            })
            .filter(Boolean);

          // Fetch all training data in one query
          let trainingDataMap = new Map();
          if (trainingIds.length > 0) {
            const { data: trainingsData, error: trainingsError } = await supabase
              .from("training_catalogue_data")
              .select("*")
              .in("id", trainingIds)
              .neq("status", "archived");

            if (trainingsError) {
              console.error("Error fetching training data:", trainingsError.message);
            } else if (trainingsData) {
              let enrichedTrainings = trainingsData;
              if (companyId) {
                enrichedTrainings = await checkTrainingsCriticalStatus(trainingsData, companyId);
              }
              enrichedTrainings.forEach(training => {
                trainingDataMap.set(training.id, training);
              });
            }
          }

          // Map selected trainings with full training data
          selected_training = form.selectedTrainings.map((trainingItem) => {
            const trainingObj = trainingItem.training || trainingItem;
            const skillReqId = trainingItem.skill_requirements_id || trainingItem.skillsRequirementId || 
                              (trainingObj.skill_requirements_id) || null;
            
            const trainingId = trainingObj.training_id || trainingObj.id;
            const fullTrainingData = trainingDataMap.get(trainingId) || {};

            return {
              training_id: trainingId || null,
              catalogue_id: fullTrainingData.catalogue_id || trainingObj.catalogue_id || null,
              cost: fullTrainingData.cost || trainingObj.cost || null,
              name: fullTrainingData.name || trainingObj.name || null,
              training_type: fullTrainingData.training_type || trainingObj.type || null,
              training_category: fullTrainingData.training_category || trainingObj.training_category || null,
              provider: fullTrainingData.provider || trainingObj.provider || null,
              is_critical: fullTrainingData.is_critical !== undefined ? fullTrainingData.is_critical : (trainingObj.is_critical || false),
              nqf_aligned: fullTrainingData.nqf_aligned !== undefined ? fullTrainingData.nqf_aligned : (trainingObj.nqf_aligned || false),
              targeted_skills: fullTrainingData.targeted_skills || trainingObj.targeted_skills || null,
              skillsRequirementId: skillReqId,
            };
          });
        }
      } else if (form.skill_requirements && Array.isArray(form.skill_requirements)) {
        // Fallback: Extract from skill_requirements
        form.skill_requirements.forEach((skill) => {
          const selectedTrainingsInSkill = skill.selected_trainings || [];
          if (Array.isArray(selectedTrainingsInSkill)) {
            selectedTrainingsInSkill.forEach((training) => {
              const trainingData = training.training_catalogue_data || training;
              selected_training.push({
                training_id: trainingData.id || null,
                catalogue_id: trainingData.catalogue_id || null,
                cost: trainingData.cost || null,
                name: trainingData.name || null,
                training_type: trainingData.type || null,
                training_category: trainingData.training_category || null,
                provider: trainingData.provider || null,
                is_critical: trainingData.is_critical || false,
                nqf_aligned: trainingData.nqf_aligned || false,
                targeted_skills: trainingData.targeted_skills || null,
                skillsRequirementId: skill.id || skill.skill_requirements_id || null,
              });
            });
          }
        });
      }
      
      // Build the final structure exactly as specified
      return {
        emp_data: emp_data,
        form_data: {
          notes: notes,
          approved: approved,
          long_term_goal: long_term_goal,
          short_term_goal: short_term_goal,
          skills_required: skills_required,
          skillRequirements: skillRequirements,
          selected_training: selected_training,
        },
      };
    })
  );
};

/**
 * Transforms the payload to optimized format for AI processing
 * @param {Object} payload - Original payload object
 * @returns {Object} Optimized payload object
 */
const optimizePayload = (payload, disabilityConfig = {}, employmentTypes = []) => {
  const { disabilityOptions = [], disabilityFieldName = null } = disabilityConfig;
  // Get BBEE type to determine which fields to include
  const bbeeType = (payload.plan_optimizer_config?.bbee_type || "").toLowerCase();
  const isBudgetType = bbeeType === "budget";
  const isHeadcountType = bbeeType === "headcount";

  // Remove duplicate demographics (merge if needed)
  const demographics = {};
  (payload.plan_optimizer_config?.demographic_wise_config || []).forEach((demo) => {
    const key = demo.race?.toLowerCase();
    if (key) {
      if (demographics[key]) {
        // Merge if duplicate found - take maximum values
        if (isBudgetType) {
          demographics[key].allocation.male = Math.max(
            Number(demographics[key].allocation.male) || 0,
            Number(demo.budget?.male) || 0
          );
          demographics[key].allocation.female = Math.max(
            Number(demographics[key].allocation.female) || 0,
            Number(demo.budget?.female) || 0
          );
        }
        if (isHeadcountType) {
          demographics[key].headcount.male = Math.max(
            Number(demographics[key].headcount.male) || 0,
            Number(demo.head_count?.male) || 0
          );
          demographics[key].headcount.female = Math.max(
            Number(demographics[key].headcount.female) || 0,
            Number(demo.head_count?.female) || 0
          );
        }
      } else {
        // Create new entry
        demographics[key] = {};
        if (isBudgetType) {
          demographics[key].allocation = {
            male: Number(demo.budget?.male) || 0,
            female: Number(demo.budget?.female) || 0,
          };
        }
        if (isHeadcountType) {
          demographics[key].headcount = {
            male: Number(demo.head_count?.male) || 0,
            female: Number(demo.head_count?.female) || 0,
          };
        }
      }
    }
  });

  // Add Disability to demographics (from separate disability config)
  // Disability is total only (no male/female split)
  if (isBudgetType && payload.plan_optimizer_config?.disability?.budget?.total) {
    const disabilityTotal = Number(payload.plan_optimizer_config.disability.budget.total) || 0;
    demographics["disability"] = {
      allocation: {
        total: disabilityTotal,
      },
    };
  } else if (isHeadcountType && (payload.plan_optimizer_config?.disability?.head_count?.total || payload.plan_optimizer_config?.disability?.budget?.total)) {
    // Use head_count.total if available, otherwise fallback to budget.total
    const disabilityTotal = Number(payload.plan_optimizer_config.disability.head_count?.total || payload.plan_optimizer_config.disability.budget?.total) || 0;
    demographics["disability"] = {
      headcount: {
        total: disabilityTotal,
      },
    };
  }

  // Unemployed / None: single total (part of BBEE Current Plan)
  if (isBudgetType && payload.plan_optimizer_config?.none?.budget?.total != null) {
    const noneTotal = Number(payload.plan_optimizer_config.none.budget.total) || 0;
    demographics["none"] = {
      allocation: {
        total: noneTotal,
      },
    };
  } else if (
    isHeadcountType &&
    (payload.plan_optimizer_config?.none?.head_count?.total != null ||
      payload.plan_optimizer_config?.none?.budget?.total != null)
  ) {
    const noneTotal =
      Number(
        payload.plan_optimizer_config.none.head_count?.total ??
          payload.plan_optimizer_config.none.budget?.total,
      ) || 0;
    demographics["none"] = {
      headcount: {
        total: noneTotal,
      },
    };
  }

  // Transform employees from training_survey_forms
  const employees = (payload.training_survey_forms || []).map((form) => {
    const empData = form.emp_data || {};
    const formData = form.form_data || {};
    
    // Extract demographic fields (check both emp_data and original form data)
    const originalEmpData = form.empData || {};
    
    // Build employee name with proper capitalization (check both emp_data and original form data)
    const gender = (empData.gender || originalEmpData.gender || "").toLowerCase();
    const race = (empData.race || originalEmpData.race || "").toLowerCase();
    const disability = resolveDisability({
      record: {
        ...originalEmpData,
        ...empData,
        data: originalEmpData.data ?? empData.data,
      },
      disabilityOptions,
      disabilityFieldName,
    });

    // Transform skill requirements
    const skillRequirements = (formData.skillRequirements || []).map((sr) => {
      const cleaned = {};
      if (sr.id) cleaned.id = sr.id;
      if (sr.skills_required) cleaned.skill = sr.skills_required;
      if (sr.priority_level) cleaned.priority = sr.priority_level;
      if (sr.program_duration) {
        // Simplify program duration - extract key word (e.g., "short program" -> "short")
        const duration = sr.program_duration.toLowerCase().trim();
        cleaned.program_duration = duration.includes("short") 
          ? "short" 
          : duration.includes("long") 
          ? "long" 
          : duration.includes("medium")
          ? "medium"
          : duration;
      }
      if (sr.skills_requirement_motivation) cleaned.motivation = sr.skills_requirement_motivation;
      return cleaned;
    });

    // Transform selected training; for unemployed, first training cost = programme cost + stipend (same as Generate Plan)
    const stipendAmount = Number(empData.stipend_amount ?? originalEmpData.stipend_amount ?? 0) || 0;
    const isUnemployed = isUnemployedEmploymentType(
      employmentTypes,
      empData.employment_type ?? originalEmpData.employment_type,
    );
    const trainingSelected = (formData.selected_training || []).map((st, index) => {
      const cleaned = {};
      if (st.training_id) cleaned.training_id = st.training_id;
      if (st.name) cleaned.name = st.name;
      if (st.provider) cleaned.provider = st.provider;
      let cost = st.cost !== null && st.cost !== undefined ? Number(st.cost) || 0 : 0;
      if (isUnemployed && index === 0) cost += stipendAmount;
      cleaned.cost = cost;
      if (st.training_category) cleaned.training_category = st.training_category;
      if (st.is_critical !== null && st.is_critical !== undefined) cleaned.is_critical = st.is_critical;
      if (st.nqf_aligned !== null && st.nqf_aligned !== undefined) cleaned.nqf_aligned = st.nqf_aligned;
      if (st.skillsRequirementId) cleaned.linked_skill_requirement = st.skillsRequirementId;
      return cleaned;
    });

    return {
      emp_id: empData.emp_id || originalEmpData.emp_id || originalEmpData.id || null,
      // name: fullName || null,
      gender: gender || null,
      race: race || null,
      disability: disability || null,
      employment_type: empData.employment_type || originalEmpData.employment_type || null,
      unemployed: isUnemployed,
      region: empData.region || null,
      skill_requirements: skillRequirements || null,
      training_selected: trainingSelected || null,
      employee_preferences: {
        short_term_goal: formData.short_term_goal || null,
        long_term_goal: formData.long_term_goal || null,
        notes: formData.notes || null,
      },
    }    
  });

  // Transform training categories to keyed-object
  const trainingCategories = {};
  (payload.plan_optimizer_config?.training_type_wise_config || []).forEach((cat) => {
    const maleCount = Number(cat.head_count?.male) || 0;
    const femaleCount = Number(cat.head_count?.female) || 0;
    const totalHeadcount = maleCount + femaleCount;
    const categoryType = cat.training_type?.replace("Category ", "") || cat.training_type || "";
    
    if (categoryType) {
      trainingCategories[categoryType] = {
        budget: Number(cat.budget) || 0,
        headcount: totalHeadcount,
      };
    }
  });

  // Transform departments
  const departments = (payload.plan_optimizer_config?.department_wise_config || []).map((dept) => ({
    dept_id: dept.dept_id || null,
    budget: Number(dept.budget) || 0,
  }));

  // Build masterTrainingData from unique trainings across all employees
  const masterTrainingMap = new Map();
  employees.forEach((emp) => {
    (emp.training_selected || []).forEach((training) => {
      if (training.training_id && !masterTrainingMap.has(training.training_id)) {
        masterTrainingMap.set(training.training_id, {
          training_id: training.training_id,
          name: training.name || "",
          provider: training.provider || "",
          cost: training.cost !== null && training.cost !== undefined ? Number(training.cost) : 0,
          is_critical: training.is_critical !== null && training.is_critical !== undefined ? training.is_critical : false,
          nqf_aligned: training.nqf_aligned !== null && training.nqf_aligned !== undefined ? training.nqf_aligned : false,
        });
      }
    });
  });
  const masterTrainingData = Array.from(masterTrainingMap.values());
  // Build optimized payload
  return {
    strategy: {
      training: {
        summary: payload.training_strategy?.strategic_input_summary || "",
        strategic_goals: payload.training_strategy?.strategic_goals || [],
        critical_skills: payload.training_strategy?.critical_skills || [],
      },
    },
    optimizer_config: {
      allocation_type: {
        bbee: (payload.plan_optimizer_config?.bbee_type || "").toLowerCase(),
        training_category: (payload.plan_optimizer_config?.training_category_type || "").toLowerCase(),
      },
      priority_weights: {
        critical_skills: Number(payload.plan_optimizer_config?.critical_skills_priority) || 0,
      },
      total_budget: Number(payload.plan_optimizer_config?.total_budget) || 0,
      departments: departments,
      demographics: demographics,      training_categories: trainingCategories,
    },
    employees: employees,
    masterTrainingData: masterTrainingData,
  };
};

/**
 * Whether the company has forms in Recommended/Approved status for plan optimization.
 * Matches the eligibility filter used in buildPlanOptimizerPayload.
 */
export const hasPlanOptimizerEligibleTrainingData = async (companyId) => {
  if (!companyId) return false;

  const { data, error } = await supabase.rpc("get_users_with_training_and_manager", {
    companyid: companyId,
    departmentid: null,
    empid: null,
    viewall: true,
    statusfilter: SKILLS_PLANNER_FORM_STATUS_FILTER.BOTH,
  });

  if (error) {
    console.error("Error checking plan optimizer training data:", error.message);
    return false;
  }

  const eligibleStatuses = new Set([
    trainingFormStatus.APPROVED,
    trainingFormStatus.RECOMMENDED,
  ]);

  return (data || []).some(
    (row) =>
      eligibleStatuses.has(row.status) || eligibleStatuses.has(row.Status),
  );
};

/**
 * Fetches all required data and builds the JSON payload for Plan Optimizer AI call
 * @param {Object} params - Parameters object
 * @param {string} params.companyId - Company ID
 * @param {string} params.yearId - Year ID
 * @param {Object} params.bbeeValues - BBEE values from Plan Optimizer modal
 * @param {Object} params.trainingCategoryValues - Training category values from Plan Optimizer modal
 * @param {number} params.totalBudget - Total budget
 * @param {string} params.bbeeType - BBEE type ("Budget" or "Headcount")
 * @param {string} params.trainingCategoryType - Training category type ("Budget" or "Headcount")
 * @param {number} params.criticalSkillsPriority - Critical skills priority
 * @param {Object} params.disabilityData - Disability data configuration
 * @returns {Promise<Object>} Complete AI payload object in optimized format
 */
export const buildPlanOptimizerPayload = async ({
  companyId,
  yearId,
  bbeeValues,
  trainingCategoryValues,
  totalBudget,
  bbeeType,
  trainingCategoryType,
  criticalSkillsPriority,
  disabilityData = { total: "0" }, // Disability data (total only)
  unemployedData = { total: "0" }, // Unemployed / None (single total; part of BBEE Current Plan)
}) => {
  
  try {
    const disabilityConfig = await loadDisabilityConfigForCompany(companyId);
    const employmentTypes = await loadEmploymentTypesForCompany(companyId);

     // Fetch strategic input along with its goals in a single query using the `select` relational syntax
    let strategicInput = null;
    let strategicGoals = [];
    const { data: strategyWithGoals, error: strategyError } = await supabase
      .from("strategic_input")
      .select("strategic_input_summary, strategic_input_goals(goal, benefits, timeline, status)")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (strategyError) {
      console.error("Error fetching strategic input with goals:", strategyError.message);
    }

    if (strategyWithGoals) {
      strategicInput = { ...strategyWithGoals };
      // Only include "active" goals just like before
      strategicGoals = (strategyWithGoals.strategic_input_goals || []).filter(g => g.status === "active");
    }

    // 3. Fetch Critical Skills
    const { data: criticalSkills, error: criticalSkillsError } = await supabase
      .from("critical_skills")
      .select("skill_name, description, level, importance")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (criticalSkillsError) {
      console.error("Error fetching critical skills:", criticalSkillsError.message);
    }
      
    // 5. Plan Optimizer: true-latest Recommended + Approved only
    const { data: employeeDetailsRawUnfiltered, error: employeeError } =
      await supabase.rpc("get_users_with_training_and_manager", {
        companyid: companyId,
        departmentid: null,
        empid: null,
        viewall: true,
        statusfilter: SKILLS_PLANNER_FORM_STATUS_FILTER.BOTH,
      });

    if (employeeError && employeeError.code !== "PGRST116") {
      throw employeeError;
    }

    const planOptimizerEligibleStatuses = new Set([
      trainingFormStatus.APPROVED,
      trainingFormStatus.RECOMMENDED,
    ]);
    const employeeDetailsRaw = (employeeDetailsRawUnfiltered || []).filter(
      (row) =>
        planOptimizerEligibleStatuses.has(row.status) ||
        planOptimizerEligibleStatuses.has(row.Status),
    );

    // 6. Fetch Training Survey Forms (if workflow enabled)
    // Use get_users_with_training_and_manager data instead of get_training_forms for consistency
    const { data: configData, error: configError } = await supabase
      .from("configurations")
      .select("training_need_workflow")
      .eq("company_id", companyId)
      .eq("year_id", yearId)
      .single();

    let trainingSurveyForms = [];
    if (configData?.training_need_workflow) {
      // Extract form IDs from employeeDetailsRaw to fetch form details
      // Note: emp.id from get_users_with_training_and_manager is the form ID (tsf.id)
      const formIds = (employeeDetailsRaw || [])
        .map(emp => emp.id)
        .filter(Boolean);

      // Fetch form details (notes, goals) for all forms
      let formDetailsMap = new Map();
      if (formIds.length > 0) {
        const { data: formsData, error: formsError } = await supabase
          .from("training_survey_form")
          .select("id, notes, short_term_goal, long_term_goal, approved")
          .in("id", formIds)
          .eq("status", statuses.ACTIVE);

        if (!formsError && formsData) {
          formsData.forEach(form => {
            formDetailsMap.set(form.id, form);
          });
        }
      }

      // Also fetch employee data from company_user to get full employee details (race, gender, etc.)
      // Use emp_id (text) to query since get_users_with_training_and_manager returns Employee Id as text
      const employeeUuids = (employeeDetailsRaw || [])
        .map(emp => emp["Employee UUID"])
        .filter(Boolean);
      
      let employeeDataMap = new Map();

      //TODO - we can load gender, race, disability_status, employment_type directly from get_users_with_training_and_manager, 
      // for that we need to return additional fields from above function
      if (employeeUuids.length > 0) {
        const { data: employeesData, error: employeesError } = await supabase
          .from("company_user")
          .select("id, emp_id, gender, race, disability_status, employment_type, stipend_amount, data")
          .in("id", employeeUuids)
          .eq("company_id", companyId)
          .eq("status", statuses.ACTIVE);

        if (!employeesError && employeesData) {
          employeesData.forEach(emp => {
            employeeDataMap.set(emp.id, emp);
          });
        }
      }
    
      // Optimize by batching all unique training_ids before mapping, and querying once for all at top level

      // Gather ALL unique training_ids across ALL employees/skills
      const allTrainingIdsSet = new Set();
      (employeeDetailsRaw || []).forEach((emp) => {
        (emp.skills || []).forEach((skill) => {
          (skill.selectedTrainings || []).forEach((training) => {
            const id = training.training_id || training.id;
            if (id) allTrainingIdsSet.add(id);
          });
        });
      });
      const allTrainingIds = Array.from(allTrainingIdsSet);
      // Fetch all unique training_catalogue_data in one query
      let globalTrainingDataMap = new Map();
      if (allTrainingIds.length > 0) {
        const { data: trainingsData, error: trainingsError } = await supabase
          .from("training_catalogue_data")
          .select("*")
          .in("id", allTrainingIds)
          .neq("status", "archived");

        if (!trainingsError && trainingsData) {
          const enrichedTrainings = await checkTrainingsCriticalStatus(
            trainingsData,
            companyId,
          );
          enrichedTrainings.forEach((training) => {
            globalTrainingDataMap.set(training.id, training);
          });
        }
      }

      // Now do transformation, WITHOUT per-employee training_catalogue query
      const transformedForms = (employeeDetailsRaw || []).map((emp) => {
        // Get form details (emp.id is the form ID)
        const formDetails = formDetailsMap.get(emp.id) || {};
        
        // Get employee data (use Employee UUID to lookup)
        const employeeUuId = emp["Employee UUID"];
        const employeeFullData = employeeDataMap.get(employeeUuId) || {};
        // Plan key must stay UUID (training_survey_form.emp_id / ATP joins).
        // HR Personnel Number lives separately when present (may be null for unemployed).
        const hrEmpId =
          employeeFullData.emp_id ||
          emp["Employee Id"] ||
          emp["Employee ID"] ||
          null;

        // Build empData structure expected by FormatTrainingSurveyForms
        const empData = {
          id: employeeFullData.id || employeeUuId || emp.id,
          emp_id: employeeUuId || employeeFullData.id || null,
          hr_emp_id: hrEmpId,
          race: employeeFullData.race,
          gender: employeeFullData.gender,
          data: employeeFullData.data,
          employment_type: employeeFullData.employment_type,
          stipend_amount: employeeFullData.stipend_amount,
          disability_status: resolveDisability({
            record: employeeFullData,
            disabilityOptions: disabilityConfig.disabilityOptions,
            disabilityFieldName: disabilityConfig.disabilityFieldName,
          }),
        };

        // Build form structure expected by FormatTrainingSurveyForms
        const skillRequirements = (emp.skills || []).map((skill) => ({
          id: skill.id,
          skills_required: skill.skills_required,
          priority_level: skill.priority_level,
          program_duration: skill.program_duration,
          skills_requirement_motivation: skill.skills_requirement_motivation,
        }));

        // Map selected trainings with data from globalTrainingDataMap
        const selectedTrainings = (emp.skills || []).flatMap((skill) => 
          (skill.selectedTrainings || []).map((training) => {
            const trainingId = training.training_id || training.id;
            const fullTrainingData = globalTrainingDataMap.get(trainingId) || {};

            return {
              id: trainingId,
              training_id: trainingId,
              name: training.training_name || training.name || fullTrainingData.name,
              cost: fullTrainingData.cost || training.cost,
              provider: fullTrainingData.provider || training.provider,
              training_category: training.allocated_training_category || training.training_category || fullTrainingData.training_category,
              training_type: training.allocated_training_type || training.training_type || fullTrainingData.training_type,
              is_critical: fullTrainingData.is_critical !== undefined ? fullTrainingData.is_critical : training.is_critical,
              nqf_aligned: fullTrainingData.nqf_aligned !== undefined ? fullTrainingData.nqf_aligned : training.nqf_aligned,
              targeted_skills: fullTrainingData.targeted_skills || training.targeted_skills,
              skillsRequirementId: skill.id,
            };
          })
        );

        return {
          empData: empData,
          forms: {
            notes: formDetails.notes || "",
            long_term_goal: formDetails.long_term_goal || "",
            short_term_goal: formDetails.short_term_goal || "",
            skills_required: null, // Not available in get_users_with_training_and_manager
          },
          skillRequirements: skillRequirements,
          selectedTrainings: selectedTrainings,
        };
      });
      // format training survey forms using the transformed data
      trainingSurveyForms = await FormatTrainingSurveyForms(transformedForms, disabilityConfig, companyId);
    }

    // 8. Build Demographic Wise Config from Plan Optimizer values
    const demographicWiseConfig = POPULATION_GROUPS.map((group) => {
      const values = bbeeValues[group.key] || { male: { c: 0, t: 0 }, female: { c: 0, t: 0 } };
      return {
        race: group.key.toLowerCase(),
        budget: {
          male: values.male?.c || 0,
          female: values.female?.c || 0,
        },
        head_count: {
          male: values.male?.c || 0,
          female: values.female?.c || 0,
        },
      };
    });

    // Disability is stored separately in the config, not in demographic_wise_config array
    // It will be added to the optimizer_config separately

    // 9. Build Training Type Wise Config from Plan Optimizer values
    const trainingTypeWiseConfig = TRAINING_CATEGORY_OPTIONS.map((category) => {
      const values = trainingCategoryValues[category.value] || { c: 0, t: 0 };
      
      // Handle both old format (male/female) and new format (single value)
      let totalCategoryBudget = 0;
      let headCountMale = 0;
      let headCountFemale = 0;
      
      if (values.male && values.female) {
        // Old format - sum male and female
        totalCategoryBudget = (values.male?.c || 0) + (values.female?.c || 0);
        headCountMale = values.male?.c || 0;
        headCountFemale = values.female?.c || 0;
      } else {
        // New format - single value
        totalCategoryBudget = values.c || 0;
        headCountMale = values.c || 0;
        headCountFemale = 0;
      }
      
      const percentage =
        totalBudget > 0
          ? parseFloat(((totalCategoryBudget / totalBudget) * 100).toFixed(2))
          : 0;

      return {
        training_type: category.value,
        percentage: percentage,
        budget: totalCategoryBudget,
        head_count: {
          male: headCountMale,
          female: headCountFemale,
        },
      };
    });
     // 10. Build Complete Training Strategy Object
    const trainingStrategy = {
      strategic_input_summary: strategicInput?.strategic_input_summary || "",
      strategic_goals: strategicGoals || [],
      critical_skills: criticalSkills || [],
    };

    // 11. Build the complete JSON payload
    const aiPayload = {
      training_strategy: trainingStrategy,
      plan_optimizer_config: {
        bbee_type: bbeeType,
        training_category_type: trainingCategoryType,
        critical_skills_priority: criticalSkillsPriority,
        total_budget: parseFloat(totalBudget) || 0,  
        demographic_wise_config: demographicWiseConfig,
        training_type_wise_config: trainingTypeWiseConfig,
        // Add Disability separately (total only, no male/female split)
        disability: {
          budget: {
            total: parseFloat(disabilityData.total) || 0,
          },
        },
        // Unemployed / None (single total; included in BBEE Current Plan)
        none: {
          budget: {
            total: parseFloat(unemployedData.total) || 0,
          },
        },
      },
      ...(configData?.training_need_workflow && { training_survey_forms: trainingSurveyForms }),
    };

    // 12. Transform to optimized format
    const expandedPayload = optimizePayload(aiPayload, disabilityConfig, employmentTypes);
    const compactPayload = optimizeTrainingPayload(expandedPayload);

    // Expanded payload is easier for AI to reason over (named fields + costs).
    // Kept on compact object for direct OpenAI calls; stripped before DB save.
    compactPayload._expandedPayload = expandedPayload;

    return compactPayload;
  } catch (error) {
    console.error("Error in buildPlanOptimizerPayload:", error.message);
    throw error;
  }
};

/** Remove internal fields before persisting payload to the database. */
export const stripInternalPayloadFields = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  const { _expandedPayload, ...rest } = payload;
  return rest;
};


/**
 * Further optimizes the payload by restructuring data into arrays to reduce prompt size
 * @param {Object} oldJson - Optimized payload from optimizePayload function
 * @returns {Object} Further optimized payload with array-based structure
 */
export const optimizeTrainingPayload = (oldJson) => {
  const training = oldJson.strategy?.training || {};
  const optimizer = oldJson.optimizer_config || {};
  const bbeeType = optimizer.allocation_type?.bbee || "";
  const isBudgetType = bbeeType === "budget";
  const isHeadcountType = bbeeType === "headcount";
 
  // Use provided masterTrainingData or extract unique trainings from employees
  let masterTrainingData = oldJson.masterTrainingData || [];
   
  return {
    _format: {
      strategic_goals: ["goal", "status", "timeline"],
      critical_skills: ["skill_name", "level", "importance"],
      demographics: isBudgetType ? ["male_budget", "female_budget"] : ["male_headcount", "female_headcount"],
      demographics_disability: isBudgetType ? ["total_budget"] : ["total_headcount"],
      demographics_none: isBudgetType ? ["total_budget"] : ["total_headcount"],
      training_categories: ["budget"],
      masterTrainingData: ["training_id", "name", "provider", "cost", "is_critical", "nqf_aligned"],
      employee_profile: ["gender", "race", "disability", "employment_type"],
      skill_requirements: ["id", "skill", "priority", "program_duration", "motivation"],
      training_selected: ["training_id", "training_category", "linked_skill_requirement_id"]
    },
 
    strategy: {
      training: {
        summary: training.summary || "",
        strategic_goals: (training.strategic_goals || []).map(g => [
          g.goal || "",
          g.status || "",
          g.timeline || ""
        ]),
        critical_skills: (training.critical_skills || []).map(s => [
          s.skill_name || "",
          s.level || "",
          s.importance || ""
        ])
      }
    },
 
    optimizer_config: {
      allocation_type: optimizer.allocation_type ? [
        optimizer.allocation_type.bbee || "",
        optimizer.allocation_type.training_category || ""
      ] : [],
      priority_weights: optimizer.priority_weights || {},
      total_budget: optimizer.total_budget || 0,
      demographics: Object.entries(optimizer.demographics || {}).reduce(
        (acc, [key, val]) => {
          // Skip disability / none — extracted separately
          if (key === "disability" || key === "none" || key === "unemployed") {
            return acc;
          }
          // Regular demographics (race-based) with male/female split
          if (isBudgetType) {
            acc[key] = val.allocation?.male !== undefined && val.allocation?.female !== undefined
              ? [val.allocation.male, val.allocation.female]
              : [0, 0];
          } else if (isHeadcountType) {
            acc[key] = val.headcount?.male !== undefined && val.headcount?.female !== undefined
              ? [val.headcount.male, val.headcount.female]
              : [0, 0];
          } else {
            // Fallback: try to use whatever is available
            if (val.allocation?.male !== undefined && val.allocation?.female !== undefined) {
              acc[key] = [val.allocation.male, val.allocation.female];
            } else if (val.headcount?.male !== undefined && val.headcount?.female !== undefined) {
              acc[key] = [val.headcount.male, val.headcount.female];
            } else {
              acc[key] = [0, 0];
            }
          }
          return acc;
        },
        {}
      ),
      demographics_disability: (() => {
        // Extract disability separately
        const disability = optimizer.demographics?.disability;
        if (!disability) {
          return {};
        }
        // Disability is total only (no male/female split)
        let disabilityValue = 0;
        if (isBudgetType && disability.allocation?.total !== undefined) {
          disabilityValue = disability.allocation.total;
        } else if (isHeadcountType && disability.headcount?.total !== undefined) {
          disabilityValue = disability.headcount.total;
        } else if (isBudgetType && disability.allocation?.total === undefined && disability.headcount?.total !== undefined) {
          // Fallback: use headcount.total if allocation.total not available
          disabilityValue = disability.headcount.total;
        } else if (isHeadcountType && disability.headcount?.total === undefined && disability.allocation?.total !== undefined) {
          // Fallback: use allocation.total if headcount.total not available
          disabilityValue = disability.allocation.total;
        }
        return { disability: [disabilityValue] };
      })(),
      demographics_none: (() => {
        const none =
          optimizer.demographics?.none || optimizer.demographics?.unemployed;
        if (!none) return {};
        let noneValue = 0;
        if (isBudgetType && none.allocation?.total !== undefined) {
          noneValue = none.allocation.total;
        } else if (isHeadcountType && none.headcount?.total !== undefined) {
          noneValue = none.headcount.total;
        } else if (none.allocation?.total !== undefined) {
          noneValue = none.allocation.total;
        } else if (none.headcount?.total !== undefined) {
          noneValue = none.headcount.total;
        }
        return { none: [noneValue] };
      })(),
      training_categories: Object.entries(optimizer.training_categories || {}).reduce(
        (acc, [key, val]) => {
          // Remove "Category " prefix if present (e.g., "Category A" -> "A")
          const categoryKey = key.replace(/^Category\s+/i, "");
          // Only include budget (no headcount) as per user requirement
          acc[categoryKey] = [val.budget !== undefined ? Number(val.budget) : 0];
          return acc;
        },
        {}
      )
    },
 
    masterTrainingData: masterTrainingData.map(t => [
      t.training_id,
      t.name || "",
      t.provider || "",
      t.cost,
      t.is_critical,
      t.nqf_aligned
    ]),
 
    employees: (oldJson.employees || []).map(emp => ({
      emp_id: emp.emp_id || null,
      unemployed: Boolean(emp.unemployed),
      profile: [
        emp.gender || null,
        emp.race || null,
        emp.disability || false,
        emp.employment_type || null
      ],
      skill_requirements: (emp.skill_requirements || []).map(sr => [
        sr.id || null,
        sr.skill || "",
        sr.priority || "",
        sr.program_duration || "",
        sr.motivation || ""
      ]),
      training_selected: (emp.training_selected || []).map(ts => [
        ts.training_id || null,
        ts.training_category || null,
        ts.linked_skill_requirement || ts.linked_skill_requirement_id || null
      ])
    }))
  };
};
